import React from 'react';
import { SuButton } from '@amzn/seller-university-ui-components';

export interface CredentialCompletionPanelProps {
  /** Earned-state headline shown above the certificate download action. */
  message: string;
  /** Visible label for the certificate download button. */
  downloadLabel: string;
  /** URL of the certificate PDF; used as both the link target and the download filename. */
  certificateUrl: string;
}

/**
 * True only for http(s) or relative certificate URLs. Guards the download link against a
 * non-http(s) scheme (e.g. javascript:/data:) reaching the anchor href; an unsafe or
 * unparseable value hides the button rather than rendering an executable link.
 */
const isSafeCertificateUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url, window.location.origin);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * Earned-state panel shown on the credential page once the credential is awarded:
 * a headline plus a primary button that downloads the certificate. The download button is
 * omitted when the certificate URL fails the http(s)/relative scheme check.
 */
export const CredentialCompletionPanel: React.FC<CredentialCompletionPanelProps> = ({
  message,
  downloadLabel,
  certificateUrl,
}) => (
  <div className="mx-auto mt-su-space-05 flex flex-col items-center gap-su-space-05 rounded bg-su-background-message-secondary p-su-space-07 sm:w-11/12">
    <p className="!m-su-space-00 text-su-heading-200 !font-bold">{message}</p>
    {isSafeCertificateUrl(certificateUrl) && (
      <SuButton
        variant="primary"
        label={downloadLabel}
        icon={<span aria-hidden="true">&#8681;</span>}
        linkProps={{
          href: certificateUrl,
          download: certificateUrl,
          refTag: 'su_credential_certificate_download',
        }}
      />
    )}
  </div>
);

export default CredentialCompletionPanel;
